import React from 'react';
import {
  View,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from './Hoverable';
import { GlassCard } from './GlassCard';
import { GlassStatusBadge, type GlassStatusVariant } from '@/components/admin/redesign';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';

const TILE_RADIUS = 14;

export type StatTileTone = 'success' | 'warn' | 'error' | 'muted' | 'info';

export interface StatTileTrend {
  tone: StatTileTone;
  text: string;
}

export interface StatTileProps {
  label: string;
  value: string;
  caption?: string;
  captionFallback?: string;
  trend?: StatTileTrend;
  onPress?: () => void;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Map the resident-side semantic tone vocabulary onto the admin
 * `GlassStatusBadge` variant set so the badge can stay the single source of
 * truth for status colouring across both audiences.
 */
const TONE_TO_VARIANT: Record<StatTileTone, GlassStatusVariant> = {
  success: 'success',
  warn: 'warning',
  error: 'error',
  muted: 'neutral',
  info: 'info',
};

/**
 * Compact label/value/caption summary tile, typically rendered inside a
 * `KPISummaryBar`. Wraps the shared `GlassCard` so light/dark theming and
 * border treatment stay consistent with the rest of the redesign surface.
 */
export const StatTile: React.FC<StatTileProps> = React.memo(
  ({ label, value, caption, captionFallback, trend, onPress, loading, style, testID }) => {
    const styles = useStyles();
    const { colors } = useThemeColors();

    // The pressable wrapper carries the hover shadow and lift; the border cue
    // must land on the GlassCard inside it, so the card is rendered per state.
    const renderInner = (hovered: boolean) => (
      <GlassCard style={[styles.card, style, hovered && { borderColor: colors.card.borderHover }]} padding={14} borderRadius={TILE_RADIUS}>
        <Text style={styles.label} numberOfLines={2}>
          {label}
        </Text>
        {loading ? (
          <View
            style={styles.skeleton}
            testID={testID ? `${testID}-skeleton` : undefined}
          >
            <View
              style={styles.skelValue}
              testID={testID ? `${testID}-skel-value` : undefined}
            />
            <View
              style={styles.skelTrend}
              testID={testID ? `${testID}-skel-trend` : undefined}
            />
            <View
              style={styles.skelCaption}
              testID={testID ? `${testID}-skel-caption` : undefined}
            />
          </View>
        ) : (
          <>
            <Text style={styles.value} numberOfLines={1}>
              {value}
            </Text>
            <View
              style={styles.trendRow}
              testID={testID ? `${testID}-trend-slot` : undefined}
            >
              {trend ? (
                <GlassStatusBadge
                  variant={TONE_TO_VARIANT[trend.tone]}
                  label={trend.text}
                />
              ) : null}
            </View>
            <Text style={styles.caption} numberOfLines={1}>
              {caption ?? captionFallback ?? '—'}
            </Text>
          </>
        )}
      </GlassCard>
    );

    if (onPress) {
      return (
        <Hoverable
          hover="button"
          onPress={onPress}
          testID={testID}
          style={({ pressed }) => [styles.fill, styles.fillRounded, pressed ? styles.pressed : undefined]}
          pressedOpacity={1}
        >
          {({ hovered }) => renderInner(hovered)}
        </Hoverable>
      );
    }

    // Wrap in a host View so callers can attach a testID to the tile itself
    // even when it is non-interactive — keeps the API symmetric with the
    // pressable branch.
    return <View testID={testID} style={styles.fill}>{renderInner(false)}</View>;
  },
);

StatTile.displayName = 'StatTile';

const useStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      card: {
        minWidth: 120,
        flex: 1,
      },
      fill: {
        flex: 1,
      },
      // Matches the card's radius so the hover shadow follows the rounded card.
      fillRounded: {
        borderRadius: TILE_RADIUS,
      },
      label: {
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        fontWeight: '600',
        color: colors.text.dim,
      },
      value: {
        fontSize: 22,
        fontWeight: '800',
        letterSpacing: -0.3,
        color: colors.text.primary,
        marginTop: 6,
        fontVariant: ['tabular-nums'],
      },
      caption: {
        fontSize: 11,
        color: colors.text.muted,
        marginTop: 4,
      },
      trendRow: {
        flexDirection: 'row',
        marginTop: 6,
        // matches GlassStatusBadge height so the slot reserves space when no trend is rendered
        minHeight: 22,
      },
      pressed: {
        opacity: 0.7,
      },
      skeleton: {
        marginTop: 6,
      },
      skelValue: {
        height: 22,
        borderRadius: 6,
        backgroundColor: colors.text.dim,
        opacity: 0.18,
        marginBottom: 8,
      },
      skelTrend: {
        height: 14,
        borderRadius: 6,
        backgroundColor: colors.text.dim,
        opacity: 0.14,
        width: '40%',
        marginBottom: 8,
      },
      skelCaption: {
        height: 11,
        borderRadius: 4,
        backgroundColor: colors.text.dim,
        opacity: 0.12,
        width: '60%',
      },
    }),
  );
